import type { Root as MdastRoot, Text as MdastText } from "mdast";
import type { Plugin, Transformer } from "unified";
import type { Node as UnistNode } from "unist";
import { CONTINUE, visit, Visitor } from "unist-util-visit";
import { Reference } from "../../../adapters/insights-agent/state/AppState.ts";
import {
  CITATION_MARKER_PREFIX,
  CITATION_MARKER_SUFFIX,
  CITATION_NODE_TYPE,
} from "./constants.ts";

export interface CitationNode extends UnistNode {
  type: "citation";
  referenceIds: string[];
  data: Reference[];
}

const citationMarkerRegex = new RegExp(
  `${CITATION_MARKER_PREFIX}([\\w,-]+)${CITATION_MARKER_SUFFIX}`,
  "g",
);

const areReferencesEqual = (ref1: Reference, ref2: Reference): boolean => {
  return (
    ref1.excerpt === ref2.excerpt &&
    ref1.title === ref2.title &&
    ref1.page === ref2.page
  );
};

const getUniqueReferences = (refsData: Reference[], ids: string[]) => {
  const uniqueRefsData: Reference[] = [];
  const uniqueRefIds: string[] = [];

  refsData.forEach((currentRef, index) => {
    const isDuplicate = uniqueRefsData.some((existingRef) =>
      areReferencesEqual(existingRef, currentRef),
    );

    if (!isDuplicate) {
      uniqueRefsData.push(currentRef);
      uniqueRefIds.push(ids[index]);
    }
  });
  return { uniqueRefsData, uniqueRefIds };
};

const makeVisitor =
  (references: Reference[]): Visitor<MdastText> =>
  (node, index, parent) => {
    if (parent === null || index === null) return;
    const textValue = node.value;
    let previousIndex = 0;
    const newChildren: Array<MdastText | CitationNode> = [];
    let match: RegExpExecArray | null;

    while ((match = citationMarkerRegex.exec(textValue)) !== null) {
      const fullMarkerText = match[0]; // e.g., %%%CITATION_0%%%
      const referenceId = match[1];
      const matchStartIndex = match.index;
      if (matchStartIndex > previousIndex) {
        // create a new text node for the text before the match
        newChildren.push({
          type: "text",
          value: textValue.substring(previousIndex, matchStartIndex),
        });
      }
      const ids = [...new Set(referenceId.split(","))];
      const refsData = ids
        .map((id) => references.find((ref) => ref.id === id))
        .filter(Boolean) as Reference[];

      const { uniqueRefsData, uniqueRefIds } = getUniqueReferences(
        refsData,
        ids,
      );

      newChildren.push({
        type: CITATION_NODE_TYPE,
        referenceIds: uniqueRefIds,
        data: uniqueRefsData,
      });
      previousIndex = matchStartIndex + fullMarkerText.length;
    } // while

    if (newChildren.length === 0) {
      return CONTINUE; // No changes needed for this node
    }

    if (previousIndex < textValue.length) {
      // create a new text node for the remaining text after the last match
      newChildren.push({
        type: "text",
        value: textValue.substring(previousIndex),
      });
    }

    if (typeof index !== "number") {
      throw new Error("Index is not a number");
    }
    // Replace the original text node with the new children
    parent!.children.splice(index, 1, ...newChildren);

    // The default behavior here would be to continue traversing the tree at index + 1.
    // However, since we just replaced the node with multiple nodes,
    // we need to adjust the index to point to the last new child.
    return index + newChildren.length;
  };

/**
 * A remark plugin that finds citation markers in text nodes and replaces
 * them with custom 'citation' nodes containing the relevant reference data.
 *
 * @param references - An array of reference data objects.
 * @returns A unified Plugin.
 */
export const remarkCitationInjector: Plugin<[Reference[]], MdastRoot> = (
  references: Reference[],
) => {
  const transformer: Transformer<MdastRoot> = (tree) => {
    visit(tree, "text", makeVisitor(references));
    return tree;
  };
  return transformer;
};
